/**
 * `Transport` sobre o WebSocket do browser (REV-05).
 *
 * Este é o ÚNICO arquivo do cliente que pode tocar em `WebSocket`. WebSocket é TCP:
 * um pacote perdido bloqueia os seguintes, e em rede móvel isso aparece como
 * micro-travada. Trocar por WebTransport depois NEVER pode tocar em gameplay,
 * então tudo que é socket fica confinado aqui, atrás dos 5 métodos.
 *
 * NEVER lance exceção em `send`: ele roda dentro do laço de frame, e uma conexão
 * morrendo NEVER pode matar o frame. Mensagem que não sai é contada, não gritada.
 */
import type { Transport } from '@game/protocol';

/** Fábrica injetável: o teste troca o socket, e o próximo transporte entra sem tocar aqui. */
export type SocketFactory = (url: string) => WebSocket;

export interface WebSocketTransportOptions {
  /**
   * Teto para o `open`. Rede móvel pendura conexão sem nunca falhar — sem teto,
   * a tela de "conectando" fica para sempre. É limite de UX, NEVER de performance:
   * por isso NEVER sai de `PERF.yaml`.
   */
  connectTimeoutMs?: number;
  createSocket?: SocketFactory;
}

/** Contadores para o HUD de perf e para `BUD-bandwidth-down`. */
export interface TransportStats {
  bytesSent: number;
  bytesReceived: number;
  /** Mensagem que não saiu (socket fechando) ou quadro que não era binário. */
  dropped: number;
}

export class TransportError extends Error {}

const DEFAULT_CONNECT_TIMEOUT_MS = 10000;

export class WebSocketTransport implements Transport {
  private socket: WebSocket | undefined;
  private messageHandler: ((bytes: Uint8Array) => void) | undefined;
  private closeHandler: ((reason: string) => void) | undefined;
  /** Definido assim que o transporte fecha; garante `onClose` exatamente uma vez. */
  private closedReason: string | undefined;
  private readonly counters: TransportStats = { bytesSent: 0, bytesReceived: 0, dropped: 0 };

  constructor(private readonly options: WebSocketTransportOptions = {}) {}

  get stats(): Readonly<TransportStats> {
    return this.counters;
  }

  connect(url: string): Promise<void> {
    if (this.socket !== undefined) {
      return Promise.reject(new TransportError('transporte já conectado'));
    }
    const create = this.options.createSocket ?? ((target: string) => new WebSocket(target));
    const socket = create(url);
    // Sem isto o browser entrega Blob e a leitura vira assíncrona — no meio do
    // laço de frame, com ordem de mensagem em risco. ALWAYS arraybuffer.
    socket.binaryType = 'arraybuffer';
    this.socket = socket;

    return new Promise<void>((resolve, reject) => {
      let settled = false;
      const timeout = setTimeout(() => {
        if (settled) return;
        settled = true;
        // Fecha antes de rejeitar: socket pendurado sem dono vaza até o GC.
        socket.close();
        this.fireClose('tempo esgotado ao conectar');
        reject(new TransportError('tempo esgotado ao conectar'));
      }, this.options.connectTimeoutMs ?? DEFAULT_CONNECT_TIMEOUT_MS);

      socket.addEventListener('open', () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        resolve();
      });

      socket.addEventListener('error', () => {
        // O evento de erro do browser NEVER diz o motivo, de propósito (privacidade).
        // O motivo real chega no `close` seguinte, quando chega.
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        reject(new TransportError(`falha ao conectar em ${url}`));
      });

      socket.addEventListener('message', (event: MessageEvent<unknown>) => {
        const data = event.data;
        if (!(data instanceof ArrayBuffer)) {
          // Quadro de texto NEVER faz parte do protocolo: proxy mexendo, ou servidor
          // de outra coisa na mesma porta. Ignora e conta — derrubar seria pior.
          this.counters.dropped += 1;
          return;
        }
        this.counters.bytesReceived += data.byteLength;
        this.messageHandler?.(new Uint8Array(data));
      });

      socket.addEventListener('close', (event: CloseEvent) => {
        clearTimeout(timeout);
        const reason = event.reason !== '' ? event.reason : `código ${String(event.code)}`;
        this.fireClose(reason);
        if (settled) return;
        settled = true;
        reject(new TransportError(`conexão fechada antes de abrir: ${reason}`));
      });
    });
  }

  send(bytes: Uint8Array): void {
    const socket = this.socket;
    if (socket === undefined || socket.readyState !== WebSocket.OPEN) {
      this.counters.dropped += 1;
      return;
    }
    // TS separa `Uint8Array<ArrayBuffer>` de `<SharedArrayBuffer>` e `send` só aceita
    // o primeiro. O codec NEVER produz view sobre memória compartilhada, e copiar
    // aqui alocaria a cada input — 30 Hz de lixo para o GC (BUD-input-rate).
    socket.send(bytes as Uint8Array<ArrayBuffer>);
    this.counters.bytesSent += bytes.byteLength;
  }

  onMessage(handler: (bytes: Uint8Array) => void): void {
    this.messageHandler = handler;
  }

  onClose(handler: (reason: string) => void): void {
    this.closeHandler = handler;
    // O fechamento pode ter acontecido antes deste registro. Sem isto, quem
    // registra tarde NEVER descobre que a conexão já morreu.
    if (this.closedReason !== undefined) handler(this.closedReason);
  }

  close(): void {
    // `close()` em socket já fechado é no-op no browser; o guarda aqui é para o
    // caso de nem ter conectado.
    this.socket?.close();
  }

  private fireClose(reason: string): void {
    if (this.closedReason !== undefined) return;
    this.closedReason = reason;
    this.closeHandler?.(reason);
  }
}
